import { useRef, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { BRANDS, PARTNER_TYPES } from "@/data/content";
import { sendForm } from "@/lib/sendForm";
import GdprLabel from "@/components/GdprLabel";
import FormError from "@/components/FormError";
import { translate } from "@/i18n";
import { useLanguage } from "@/i18n/LanguageContext";

const inputCls = "w-full bg-[#121316] border border-white/10 px-4 py-3 text-sm text-white font-light focus:border-[#C2A675] focus:outline-none transition-colors";
const labelCls = "block text-[11px] uppercase tracking-[0.16em] text-[#A8A6A0] mb-2";

export default function Profissionais() {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState({ nome: "", empresa: "", tipo_parceiro: "", email: "", telefone: "", mensagem: "", consentimento: false, website: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(false);
  const startedAt = useRef(Date.now());

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError(false);
    try {
      await sendForm({
        form: "profissionais",
        lang,
        honeypot: form.website,
        startedAt: startedAt.current,
        fields: {
          perfil: translate("pt", `pros.tipos.${form.tipo_parceiro}`),
          nome: form.nome, empresa: form.empresa, email: form.email, telefone: form.telefone,
          mensagem: form.mensagem,
        },
      });
      setSent(true);
    } catch {
      setError(true); // os dados preenchidos mantêm-se
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="professionals-page" className="pt-20">
      <Seo title={t("seo.professionals.title")} description={t("seo.professionals.desc")} />

      <section className="py-16 lg:py-20 max-w-7xl mx-auto px-6 border-b border-white/10">
        <Reveal>
          <span className="mono-tag text-xs uppercase tracking-[0.22em] text-[#C2A675]">{t("pros.tag")}</span>
          <h1 className="serif-display text-4xl sm:text-5xl lg:text-6xl font-light text-white mt-4 mb-6 leading-tight max-w-3xl">{t("pros.title")}</h1>
          {t("pros.sub").map((p, i) => (
            <p key={i} className={`text-base text-[#A8A6A0] font-light max-w-2xl leading-relaxed ${i > 0 ? "mt-3" : ""}`}>{p}</p>
          ))}
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {t("pros.areas").map((a, i) => (
            <Reveal key={a.t} delay={(i % 3) * 0.1} className="p-8 bg-[#181A1F] border border-white/5 hover:border-[#C2A675]/40 transition-colors">
              <span className="mono-tag text-[10px] text-[#C2A675] tracking-[0.2em]">0{i + 1}</span>
              <h2 className="serif-display text-xl font-light text-white mt-3 mb-3">{a.t}</h2>
              <p className="text-sm text-[#A8A6A0] leading-relaxed font-light">{a.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-16 lg:py-20 max-w-7xl mx-auto px-6 border-b border-white/10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
        <Reveal className="lg:col-span-5">
          <span className="mono-tag text-xs uppercase tracking-[0.22em] text-[#C2A675]">{t("pros.benefits_tag")}</span>
          <h2 className="serif-display text-3xl sm:text-4xl font-light text-white mt-3 leading-tight">{t("pros.benefits_title")}</h2>
        </Reveal>
        <div className="lg:col-span-7 space-y-0">
          {t("pros.benefits").map((b, i) => (
            <Reveal key={i} delay={i * 0.08} className="flex items-start gap-4 py-5 border-b border-white/10 first:border-t">
              <span className="w-1.5 h-1.5 bg-[#C2A675] mt-1.5 shrink-0" aria-hidden="true" />
              <p className="text-sm text-[#E8E6E1] font-light leading-relaxed">{b}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-16 lg:py-20 max-w-7xl mx-auto px-6 border-b border-white/10">
        <Reveal className="mb-12">
          <span className="mono-tag text-xs uppercase tracking-[0.22em] text-[#C2A675]">{t("pros.res_tag")}</span>
          <h2 className="serif-display text-3xl sm:text-4xl font-light text-white mt-3">{t("pros.res_title")}</h2>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
          {BRANDS.map((b, i) => (
            <Reveal key={b.slug} delay={i * 0.08} className="p-6 border border-white/10 bg-[#16181D]">
              <h3 className="serif-display text-lg font-light text-white mb-1">{b.name}</h3>
              <p className="text-[11px] text-[#A8A6A0] font-light">{t(`brand.${b.slug}.tag`)}</p>
              <ul className="mt-5 space-y-2 text-xs">
                <li><a href={b.officialSite} target="_blank" rel="noopener noreferrer" data-testid={`pros-site-${b.slug}`} className="inline-flex items-center gap-2 text-[#C2A675] hover:text-white transition-colors">{t("pros.res_site")} <ArrowUpRight size={12} aria-hidden="true" /></a></li>
                <li><a href={b.catalogUrl} target="_blank" rel="noopener noreferrer" data-testid={`pros-docs-${b.slug}`} className="inline-flex items-center gap-2 text-[#C2A675] hover:text-white transition-colors">{t("pros.res_docs")} <ArrowUpRight size={12} aria-hidden="true" /></a></li>
              </ul>
            </Reveal>
          ))}
        </div>
        <Reveal>
          <p className="text-sm text-[#A8A6A0] font-light leading-relaxed max-w-2xl border-l-2 border-[#C2A675] pl-4">{t("pros.res_note")}</p>
        </Reveal>
      </section>

      <section className="py-16 lg:py-20 max-w-4xl mx-auto px-6">
        <Reveal>
          <span className="mono-tag text-xs uppercase tracking-[0.22em] text-[#C2A675]">{t("pros.form_tag")}</span>
          <h2 className="serif-display text-3xl sm:text-4xl font-light text-white mt-3 mb-10">{t("pros.form_title")}</h2>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="bg-[#17191E] border border-white/10 p-8 md:p-10">
            {sent ? (
              <div data-testid="partnership-success" role="status">
                <p className="serif-display text-2xl font-light text-[#C2A675]">{t("form.success_title")}</p>
                <p className="text-sm text-[#A8A6A0] font-light mt-3 leading-relaxed">{t("form.success_text")}</p>
                <button
                  type="button"
                  onClick={() => { setSent(false); startedAt.current = Date.now(); setForm({ nome: "", empresa: "", tipo_parceiro: "", email: "", telefone: "", mensagem: "", consentimento: false, website: "" }); }}
                  className="mt-8 px-8 py-4 border border-[#C2A675]/60 text-[#C2A675] text-xs uppercase tracking-[0.18em] hover:bg-[#C2A675] hover:text-[#121316] transition-colors"
                >
                  {t("form.another")}
                </button>
              </div>
            ) : (
            <form onSubmit={submit} className="space-y-6" data-testid="partnership-form">
              <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website} onChange={set("website")} className="hidden" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="p-nome" className={labelCls}>{t("form.nome")} *</label>
                  <input id="p-nome" data-testid="partnership-name-input" type="text" required minLength={2} value={form.nome} onChange={set("nome")} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="p-empresa" className={labelCls}>{t("form.empresa")}</label>
                  <input id="p-empresa" data-testid="partnership-company-input" type="text" value={form.empresa} onChange={set("empresa")} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="p-email" className={labelCls}>{t("form.email")} *</label>
                  <input id="p-email" data-testid="partnership-email-input" type="email" required value={form.email} onChange={set("email")} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="p-tel" className={labelCls}>{t("form.telefone")}</label>
                  <input id="p-tel" data-testid="partnership-phone-input" type="tel" value={form.telefone} onChange={set("telefone")} className={inputCls} />
                </div>
              </div>
              <div>
                <label htmlFor="p-tipo" className={labelCls}>{t("pros.tipo")} *</label>
                <select id="p-tipo" data-testid="partnership-type-select" required value={form.tipo_parceiro} onChange={set("tipo_parceiro")} className={inputCls}>
                  <option value="">—</option>
                  {PARTNER_TYPES.map((p) => <option key={p} value={p}>{t(`pros.tipos.${p}`)}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="p-msg" className={labelCls}>{t("form.mensagem")} *</label>
                <textarea id="p-msg" data-testid="partnership-message-input" rows={4} required minLength={5} value={form.mensagem} onChange={set("mensagem")} className={inputCls} />
              </div>
              <div className="flex items-start gap-3">
                <input id="p-gdpr" data-testid="partnership-gdpr-checkbox" type="checkbox" required checked={form.consentimento} onChange={set("consentimento")} className="mt-1 accent-[#C2A675]" />
                <GdprLabel htmlFor="p-gdpr" />
              </div>
              {error && <FormError />}
              <button type="submit" disabled={sending} data-testid="partnership-submit-button" className="w-full py-4 bg-[#C2A675] text-[#121316] font-medium text-xs uppercase tracking-[0.2em] hover:bg-white transition-colors disabled:opacity-50">
                {sending ? t("common.sending") : t("common.submit")}
              </button>
            </form>
            )}
          </div>
        </Reveal>
      </section>
    </div>
  );
}
